import { template } from '@babel/core';
import * as H from 'history';
import { Action, Reducer } from 'redux';
import { SurveyTemplateDataModel } from '../../Models/Surveys/SurveyDataModel';
import { AppThunkActionAsync } from './../../';
import { Result, ToxnotServiceClient } from './../../ToxnotServiceClient';

//Controller for SurveyTemplateManager.cs. Handles adding/removal of templates, as wel as setting of default templates.

//Action Types
const SetTemplateManagerData = "SET_SURVEY_TEMPLATE_MANAGER_DATA";
interface SetTemplateManagerData {
    type: typeof SetTemplateManagerData;
    value: Partial<SurveyTemplateManagerData>
}

const AddSurveyTemplate = "ADD_SURVEY_TEMPLATE";
interface AddSurveyTemplate {
    type: typeof AddSurveyTemplate;
    value: SurveyTemplateDataModel
}

const RemoveSurveyTemplates = "REMOVE_SURVEY_TEMPLATES";
interface RemoveSurveyTemplates {
    type: typeof RemoveSurveyTemplates;
    templateIdsToRemove: string[]
}


// State
export interface SurveyTemplateManagerData {
    templates: SurveyTemplateDataModel[],
    dataLoaded: boolean,
    showErrorMessage: boolean,
    selectedTemplateIds: string[],
    defaultSurveyTemplateId: string | null
}

const initialState: SurveyTemplateManagerData = {
    templates: [],
    dataLoaded: false,
    showErrorMessage: false,
    selectedTemplateIds: [],
    defaultSurveyTemplateId: null
}

type KnownAction =  AddSurveyTemplate | RemoveSurveyTemplates | SetTemplateManagerData;

//Action Acreators
const serviceClient = new ToxnotServiceClient();

export const actionCreators = {
    loadSurveyTemplates: (): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const apiResult = await serviceClient.surveyTemplateManagerBundleGet();
        if (!apiResult.success || !apiResult.data) {
            dispatch({
                type: SetTemplateManagerData,
                value: {
                    showErrorMessage: true
                }
            })
            return;
        }

        const sortedTemplates = apiResult.data.templates.orderBy(t => t.name);

        dispatch({
            type: SetTemplateManagerData,
            value: {
                showErrorMessage: false,
                dataLoaded: true,
                templates: sortedTemplates,
                defaultSurveyTemplateId: apiResult.data.defaultSurveyTemplateId
            }
        })
    },
    addNewTemplateAndRedirect: (name: string, history: H.History): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        const newTemplate: SurveyTemplateDataModel = {
            ownerId: state.appServices!.userContext!.teamOwnerId,
            templateId: null,
            name: name,
            dataRequested: {
                materialDisclosureAsked: false,
                complianceList: [],
                circularityDataAsked: false,
                sustainabilityDataAsked: false,
                embodiedCarbonDataAsked: false,
                waterUseDataAsked: false,
                carbonTransparencyDataAsked: false,
                carbonOtherDataAsked: false,
                vocDataAsked: false,
                vocTransparencyDataAsked: false,
                vocOtherDataAsked: false,
                documentsCertificationList: [],
                anyPassportDataAsked: false
            },
            bomQuestionnaireId: null,
            supplierQuestionnaireId: null,
            attestationQuestionnaireId: null,
            languageList: [],
            nativeLanguage: null,
            ccEmailList: '',
            emailSubject: '',
            emailMessage: '',
            requestInstructions: '',
            completionInstructions: '',
            languageToEmailSubjectMap: {},
            languageToEmailMessageMap: {},
            languageToRequestInstructionsMap: {},
            languageToCompletionInstructionsMap: {},
            scheduledReminders: [],
            sdsRequested: false,
            bomColumns: [],
            useManufacturerLogo: false,
            preFillBomQuestionnaireFromSummaryData: false,
            preFillSupplierQuestionnaireFromSummaryData: false,
            makeBomDataRequiredOnQuestionnaireAnswer: false,
            requireSuppliersToDiscloseRegulatoryIngredients: false,
            additionalColumnIdIdentifiers: [],
            includeAttestationLetter: false
        }

        const apiResult = await serviceClient.updateDataCollectionTemplate(newTemplate);

        if (!apiResult.success || !apiResult.data) {
            dispatch({
                type: SetTemplateManagerData,
                value: {
                    showErrorMessage: true
                }
            })
            return;
        }

        newTemplate.templateId = apiResult.data;

        dispatch({
            type: AddSurveyTemplate,
            value: newTemplate
        })

        history.push("/Survey/Template/" + encodeURIComponent(newTemplate.templateId));
    },
    removeTemplate: (templateIds: string[]): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => { 
        dispatch({
            type: RemoveSurveyTemplates,
            templateIdsToRemove: templateIds
        });
        for (const templateId of templateIds) {
            const apiResult = await serviceClient.deleteDataCollectionTemplate(templateId);
            showErrorMessageIfNeeded(dispatch, apiResult);
            if (!apiResult.success) return;
        }

    },
    setData: (changes: Omit<Partial<SurveyTemplateManagerData>, 'templates'>) => ({
        type: SetTemplateManagerData,
        value: changes
    }), 
    setDefaultSurveyTemplate: (templateId: string): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        dispatch({
            type: SetTemplateManagerData,
            value: {
                defaultSurveyTemplateId: templateId
            }
        });
        const result = await serviceClient.setSurveyTemplateAsDefault(templateId);
        showErrorMessageIfNeeded(dispatch, result);
    }
}

const showErrorMessageIfNeeded = (dispatch: (action: any) => void, apiResult: Result) => {
    if (!apiResult.success) {
        dispatch({
            type: SetTemplateManagerData,
            value: {
                showErrorMessage: true
            }
        })
    }
}

export const reducer: Reducer<SurveyTemplateManagerData> = (state: SurveyTemplateManagerData | undefined, action: Action): SurveyTemplateManagerData => { 
    if (state === undefined) {
        return initialState;
    }
    switch (action.type) {
        case SetTemplateManagerData:
            const incomingAction1 = action as SetTemplateManagerData;
            return {
                ...state,
                ...incomingAction1.value
            }
        case AddSurveyTemplate:
            const incomingAction2 = action as AddSurveyTemplate;

            const newTemplates = [...state.templates, incomingAction2.value].orderBy(t => t.name);

            return {
                ...state,
                templates: newTemplates
            }
        case RemoveSurveyTemplates:
            const incomingAction3 = action as RemoveSurveyTemplates;
            return {
                ...state,
                templates: state.templates.filter(t => !incomingAction3.templateIdsToRemove.includes(t.templateId ?? ""))
            }
        
    }
    return state;
}